import { useCallback, useMemo } from 'react';

import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

import type { ContentType } from '../content-metrics/components/filters/TypeFilter.tsx';
import type { LoadTimeRating } from '../content-metrics/components/ContentMetricsTable.tsx';

const useTypeQueryParam = createSingleQueryParamHook('type');
const useRatingQueryParam = createSingleQueryParamHook('rating');
const useSpaceQueryParam = createSingleQueryParamHook('space');
const useOwnerQueryParam = createSingleQueryParamHook('owner');

const VALID_TYPES = new Set<string>(['page', 'live-doc', 'blog']);
const VALID_RATINGS = new Set<string>(['SLOW', 'MODERATE', 'FAST']);

type QueryParamValue = string | null | undefined;

const parseCommaSeparated = (value: QueryParamValue): string[] => {
	if (!value) {
		return [];
	}
	return value.split(',').reduce<string[]>((acc, v) => {
		const trimmed = v.trim();
		if (trimmed) {
			acc.push(trimmed);
		}
		return acc;
	}, []);
};

type QueryParamResult = string | undefined;

const toQueryParam = (values: string[]): QueryParamResult => {
	return values.length > 0 ? values.join(',') : undefined;
};

const parseTypes = (raw: QueryParamValue): ContentType[] => {
	return parseCommaSeparated(raw).filter((v): v is ContentType => VALID_TYPES.has(v));
};

const parseRatings = (raw: QueryParamValue): LoadTimeRating[] => {
	return parseCommaSeparated(raw).filter((v): v is LoadTimeRating => VALID_RATINGS.has(v));
};

export const useContentMetricsFilters = () => {
	const typeQuery = useTypeQueryParam();
	const ratingQuery = useRatingQueryParam();
	const spaceQuery = useSpaceQueryParam();
	const ownerQuery = useOwnerQueryParam();
	const { setQueryParams } = useRouteActions();

	// Derive filter values directly from URL query params so all consumers
	// of this hook share the same source of truth without needing separate
	// local state that can get out of sync.
	const selectedTypes = useMemo(() => parseTypes(typeQuery), [typeQuery]);
	const selectedRatings = useMemo(() => parseRatings(ratingQuery), [ratingQuery]);
	const selectedSpaceIds = useMemo(() => parseCommaSeparated(spaceQuery), [spaceQuery]);
	const selectedOwnerIds = useMemo(() => parseCommaSeparated(ownerQuery), [ownerQuery]);

	const onTypeSelectionChange = useCallback(
		(types: ContentType[]) => {
			setQueryParams({ type: toQueryParam(types) }, true);
		},
		[setQueryParams],
	);

	const onRatingSelectionChange = useCallback(
		(ratings: LoadTimeRating[]) => {
			setQueryParams({ rating: toQueryParam(ratings) }, true);
		},
		[setQueryParams],
	);

	const onSpaceSelectionChange = useCallback(
		(spaceIds: string[]) => {
			setQueryParams({ space: toQueryParam(spaceIds) }, true);
		},
		[setQueryParams],
	);

	const onOwnerSelectionChange = useCallback(
		(ownerIds: string[]) => {
			setQueryParams({ owner: toQueryParam(ownerIds) }, true);
		},
		[setQueryParams],
	);

	return {
		selectedTypes,
		onTypeSelectionChange,
		selectedRatings,
		onRatingSelectionChange,
		selectedSpaceIds,
		onSpaceSelectionChange,
		selectedOwnerIds,
		onOwnerSelectionChange,
	};
};
